import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Space, message, Modal, Input, Tag, Spin, Alert } from 'antd';
import EFormViewerBody from '../components/EFormViewerBody';
import Cleave from 'cleave.js';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, SaveOutlined, FilePdfOutlined } from '@ant-design/icons';
import { canExportEformToPdf, exportSingleEformInstanceToPdf } from '../utils/eformPdfExportUtils';
import { useLanguage } from '../contexts/LanguageContext';
import { TimezoneUtils } from '../utils/timezoneUtils';
import { getCurrentUsername } from '../utils/userUtils';
import { validateEFormRequiredFields } from '../utils/eformValidation';
import { getFlatpickrDateFormat, parseDisplayToIso, isIsoDateString, dateToLocalIsoString, isLikelyCorruptedDateValue, injectFlatpickrIntoIframe } from '../utils/eformDateUtils';
import { getEFormApprovalLabels } from '../utils/eformApprovalLabels';
import { buildFormPreviewBlobUrl, collectFilledHtmlFromIframe } from '../utils/formPreviewIframeUtils';
import { EFormContentViewer } from '../components/MetaFlowResponseView';

const { TextArea } = Input;

/** 表單輸入欄位選擇器（含原生 date 與 data-input-type="date"） */
const FORM_INPUT_SELECTOR = 'input[type="text"], input[type="tel"], input[type="email"], input[type="number"], input[type="date"], input[type="month"], input[type="time"], input[data-input-type="currency"], input[data-input-type="date"]';

const EFormInstancePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [instance, setInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [eformExportable, setEformExportable] = useState(false);
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [approvalNote, setApprovalNote] = useState('');
  const [rejectNote, setRejectNote] = useState('');
  const [userTimezoneOffset, setUserTimezoneOffset] = useState('UTC+8');
  const [isTokenAccess, setIsTokenAccess] = useState(false);
  const [relatedAttachments, setRelatedAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [formIframeSrc, setFormIframeSrc] = useState('');
  const [formIframeLoaded, setFormIframeLoaded] = useState(0);
  const formIframeRef = useRef(null);

  const { t } = useLanguage();

  const getFormContainer = () => {
    const iframeBody = formIframeRef.current?.contentDocument?.body;
    if (iframeBody) return iframeBody;
    return document.querySelector('.form-content-card .form-content-inner')
      || document.querySelector('.form-content-inner');
  };
  const approvalActionLabels = getEFormApprovalLabels(instance, t, {
    approve: 'eformInstance.approve',
    reject: 'eformInstance.reject',
  });
  const approvalStatusLabels = getEFormApprovalLabels(instance, t, {
    approve: 'eformInstance.approved',
    reject: 'eformInstance.rejected',
  });

  // 手機檢測
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 檢查是否可匯出 PDF
  useEffect(() => {
    if (!instance) { setEformExportable(false); return; }
    const html = instance.filledHtmlCode || instance.htmlCode || '';
    setEformExportable(canExportEformToPdf(instance.fillType, html));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance?.id, instance?.fillType]);

  // 獲取用戶時區信息
  useEffect(() => {
    // 從 URL 參數中獲取 Token
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    
    if (urlToken) {
      // Token 訪問模式：使用瀏覽器時區
      setIsTokenAccess(true);
      const browserTimezone = TimezoneUtils.getBrowserTimezoneOffset();
      setUserTimezoneOffset(browserTimezone);
      console.log('Token 訪問模式，使用瀏覽器時區:', browserTimezone);
    } else {
      // 正常登錄模式：使用用戶設置的時區
      setIsTokenAccess(false);
      const userInfo = localStorage.getItem('userInfo');
      if (userInfo) {
        try {
          const parsedUserInfo = JSON.parse(userInfo);
          if (parsedUserInfo.timezone) {
            setUserTimezoneOffset(parsedUserInfo.timezone);
            console.log('登錄模式，使用用戶時區:', parsedUserInfo.timezone);
          }
        } catch (error) {
          console.error('解析用戶信息失敗:', error);
        }
      }
    }
  }, []);

  // 檢查訪問權限（支持 Token 驗證）
  useEffect(() => {
    console.log('EFormInstancePage useEffect 被調用，id:', id);
    
    const initializePage = async () => {
      // 從 URL 參數中獲取 Token
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('token');
      
      console.log('URL Token:', urlToken);
      
      // 檢查是否有 URL Token（Manual Fill 模式）
      if (urlToken) {
        validateTokenAndFetchInstance(urlToken);
      } else {
        // 傳統登入模式
        const token = localStorage.getItem('token');
        const userInfo = localStorage.getItem('userInfo');
        
        console.log('Token 存在:', !!token);
        console.log('UserInfo 存在:', !!userInfo);
        
        if (!token) {
          // 保存當前 URL 以便登入後重定向
          // 使用 useParams 獲取的 id 來構建 URL，避免 window.location 被 navigate 改變
          const redirectUrl = `/eform-instance/${id}`;
          console.log('沒有 token，保存重定向 URL:', redirectUrl);
          sessionStorage.setItem('redirectAfterLogin', redirectUrl);
          message.error(t('eformInstance.loginRequired'));
          navigate('/');
          return;
        }

        // 如果有 token 但沒有 userInfo，嘗試重新獲取用戶信息
        if (!userInfo) {
          console.log('Token 存在但 UserInfo 不存在，嘗試重新獲取用戶信息');
          try {
            const meResponse = await fetch('/api/auth/me', {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (meResponse.ok) {
              const userData = await meResponse.json();
              const fullUserInfo = {
                user_id: userData.user_id,
                account: userData.account,
                name: userData.name,
                email: userData.email,
                phone: userData.phone,
                language: userData.language,
                timezone: userData.timezone,
                avatar_url: userData.avatar_url,
                company_id: userData.company_id
              };
              
              localStorage.setItem('userInfo', JSON.stringify(fullUserInfo));
              console.log('成功重新獲取用戶信息');
            } else {
              // 如果獲取用戶信息失敗，說明 token 無效，需要重新登入
              localStorage.removeItem('token');
              sessionStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
              message.error(t('eformInstance.loginRequired'));
              navigate('/');
              return;
            }
          } catch (error) {
            console.error('重新獲取用戶信息失敗:', error);
            localStorage.removeItem('token');
            sessionStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
            message.error(t('eformInstance.loginRequired'));
            navigate('/');
            return;
          }
        }
        
        fetchInstance();
      }
    };

    initializePage();
    
    // 隱藏側邊欄
    const sider = document.querySelector('.ant-layout-sider');
    if (sider) {
      sider.style.display = 'none';
    }
    
    // 調整主內容區域
    const layout = document.querySelector('.ant-layout');
    if (layout) {
      layout.style.marginLeft = '0';
    }
    
    // 清理函數：離開頁面時恢復側邊欄
    return () => {
      if (sider) {
        sider.style.display = '';
      }
      if (layout) {
        layout.style.marginLeft = '';
      }
    };
  }, [id, navigate, t]);

  // Token 驗證並獲取實例信息
  const validateTokenAndFetchInstance = async (urlToken) => {
    try {
      setLoading(true);
      console.log('validateTokenAndFetchInstance 被調用，token:', urlToken);
      
      // 使用新的匿名端點獲取表單實例信息
      const instanceResponse = await fetch(`/api/eforminstances/${id}/public?token=${encodeURIComponent(urlToken)}`);
      
      if (instanceResponse.status === 401) {
        message.error('無效的訪問令牌或令牌已過期');
        return;
      }
      
      if (instanceResponse.status === 403) {
        message.error('您無權訪問此表單');
        return;
      }
      
      if (!instanceResponse.ok) {
        const errorData = await instanceResponse.json();
        throw new Error(errorData.error || '獲取表單實例失敗');
      }
      
      const instanceData = await instanceResponse.json();
      console.log('獲取到的實例數據:', instanceData);
      
      // 設置實例數據和 Token
      setInstance({
        ...instanceData,
        urlToken: urlToken,
        isManualFill: instanceData.fillType === 'Manual'
      });
      
    } catch (error) {
      console.error('Token 驗證錯誤:', error);
      message.error('訪問失敗: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchInstance = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      console.log('fetchInstance 被調用，id:', id);
      
      if (!token) {
        message.error(t('eformInstance.loginRequired'));
        navigate('/');
        return;
      }
      
      const response = await fetch(`/api/eforminstances/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('獲取實例響應狀態:', response.status);

      if (response.status === 401) {
        message.error(t('eformInstance.loginExpired'));
        localStorage.removeItem('token');
        localStorage.removeItem('userInfo');
        navigate('/');
        return;
      }

      if (!response.ok) {
        throw new Error(t('eformInstance.fetchFailed'));
      }

      const data = await response.json();
      console.log('獲取到的實例數據:', data);
      setInstance(data);
    } catch (error) {
      console.error('獲取表單實例錯誤:', error);
      message.error(t('eformInstance.fetchFailed') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedAttachments = useCallback(() => {
    if (!instance?.workflowExecutionId || instance.workflowExecutionId <= 0) {
      setRelatedAttachments([]);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoadingAttachments(true);
    fetch(`/api/workflowexecutions/${instance.workflowExecutionId}/media-files`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.ok ? res.json() : { data: [] })
      .then(data => setRelatedAttachments(data.data || []))
      .catch(() => setRelatedAttachments([]))
      .finally(() => setLoadingAttachments(false));
  }, [instance?.workflowExecutionId]);

  useEffect(() => {
    loadRelatedAttachments();
  }, [loadRelatedAttachments]);

  // 表單預覽：完整 HTML 用 iframe Blob URL 顯示，避免 dangerouslySetInnerHTML 無法渲染 style/body 結構
  useEffect(() => {
    if (!instance) {
      setFormIframeSrc(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
      return undefined;
    }
    const html = instance.filledHtmlCode || instance.htmlCode || '';
    if (!html || (instance.fillType === 'MetaFlows' && html.trim().startsWith('{'))) {
      setFormIframeSrc(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
      return undefined;
    }
    const url = buildFormPreviewBlobUrl(html);
    setFormIframeSrc(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [instance?.id, instance?.htmlCode, instance?.filledHtmlCode, instance?.fillType]);

  // 對金額欄位套用 Cleave.js 格式化 ($##,###.##)
  const cleaveInstancesRef = useRef([]);
  const flatpickrInstancesRef = useRef([]);
  useEffect(() => {
    if (!instance?.htmlCode) return;
    const timer = setTimeout(() => {
      const container = getFormContainer();
      if (!container) return;
      cleaveInstancesRef.current.forEach(c => c?.destroy?.());
      cleaveInstancesRef.current = [];
      flatpickrInstancesRef.current.forEach(fp => fp?.destroy?.());
      flatpickrInstancesRef.current = [];
      const currencyInputs = container.querySelectorAll('input[data-input-type="currency"]');
      currencyInputs.forEach(el => {
        try {
          const cleave = new Cleave(el, {
            numeral: true,
            numeralThousandsGroupStyle: 'thousand',
            numeralDecimalMark: '.',
            delimiter: ',',
            prefix: '$',
            numeralDecimalScale: 2,
            numeralIntegerScale: 15
          });
          cleaveInstancesRef.current.push(cleave);
        } catch (err) {
          console.warn('Cleave.js init failed for currency input:', err);
        }
      });
      const dateInputs = container.querySelectorAll('input[data-input-type="date"]');
      dateInputs.forEach(el => {
        try {
          const formatKey = el.getAttribute('data-date-format') || 'YYYY-MM-DD';
          const fpFormat = getFlatpickrDateFormat(formatKey);
          // 優先使用 data-value (ISO)，避免錯誤的 value 顯示成錯誤日期（如 20/06/2026）
          const dataValue = (el.getAttribute('data-value') || '').trim();
          let rawValue = (el.value || el.getAttribute('value') || '').trim();
          // 若為已知錯誤 pattern（如 20/06/2026 = 年前兩碼/日/年），清空不顯示，讓使用者重選
          if (!dataValue && rawValue && isLikelyCorruptedDateValue(rawValue, formatKey)) {
            rawValue = '';
            el.removeAttribute('value');
            el.value = '';
          }
          let defaultDate = null;
          if (dataValue && isIsoDateString(dataValue)) {
            defaultDate = dataValue;
          } else if (rawValue) {
            if (isIsoDateString(rawValue)) {
              defaultDate = rawValue;
            } else {
              const iso = parseDisplayToIso(rawValue, formatKey);
              if (iso) defaultDate = iso;
            }
          }
          // Flatpickr 用 dateFormat 解析字串會錯（如 "2026-03-05" 被 d/m/Y 解析成 20/06/2026），改傳 Date 物件
          const defaultDateObj = defaultDate ? new Date(defaultDate + 'T12:00:00') : null;
          const validDefaultDate = defaultDateObj && !isNaN(defaultDateObj.getTime()) ? defaultDateObj : undefined;
          const iframeBody = formIframeRef.current?.contentDocument?.body;
          const fpOptions = {
            dateFormat: fpFormat,
            defaultDate: validDefaultDate,
            allowInput: true,
            onChange(selectedDates, dateStr) {
              el.setAttribute('value', dateStr);
              if (selectedDates[0]) {
                // 用本地日期存 data-value，避免 toISOString() UTC 造成差一天
                el.setAttribute('data-value', dateToLocalIsoString(selectedDates[0]));
              }
            }
          };
          if (iframeBody && container === iframeBody) {
            fpOptions.appendTo = formIframeRef.current?.parentElement;
          }
          const fp = flatpickr(el, fpOptions);
          flatpickrInstancesRef.current.push(fp);
        } catch (err) {
          console.warn('Flatpickr init failed for date input:', err);
        }
      });
    }, 100);
    return () => {
      clearTimeout(timer);
      cleaveInstancesRef.current.forEach(c => c?.destroy?.());
      cleaveInstancesRef.current = [];
      flatpickrInstancesRef.current.forEach(fp => fp?.destroy?.());
      flatpickrInstancesRef.current = [];
    };
  }, [instance?.id, instance?.htmlCode, formIframeSrc, formIframeLoaded]);

  // 監聽來自 DocumentLibraryPage 全螢幕 Modal 的儲存請求
  useEffect(() => {
    const handleParentMessage = async (event) => {
      if (event.data?.type !== 'DOCUMENT_LIBRARY_SAVE_EFORM_REQUEST') return;
      const authToken = localStorage.getItem('token') || '';
      try {
        const filledHtmlCode = collectFilledHtmlFromForm();
        const res = await fetch(`/api/eforminstances/${id}/save`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ filledHtmlCode }),
        });
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(errBody?.error || `HTTP ${res.status}`);
        }
        event.source?.postMessage(
          { type: 'DOCUMENT_LIBRARY_SAVE_EFORM_RESPONSE', success: true },
          event.origin || '*',
        );
      } catch (err) {
        event.source?.postMessage(
          { type: 'DOCUMENT_LIBRARY_SAVE_EFORM_RESPONSE', success: false, error: err.message },
          event.origin || '*',
        );
      }
    };
    window.addEventListener('message', handleParentMessage);
    return () => window.removeEventListener('message', handleParentMessage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // 從 DOM 收集表單填寫後的 HTML（approve/reject 時保存修改過的表單欄位值）
  const collectFilledHtmlFromForm = () => {
    const iframeHtml = collectFilledHtmlFromIframe(formIframeRef);
    if (iframeHtml != null) return iframeHtml;

    const formContainer = getFormContainer();
    if (!formContainer) return null;
    const clonedContainer = formContainer.cloneNode(true);
    const origInputs = formContainer.querySelectorAll(FORM_INPUT_SELECTOR);
    const cloneInputs = clonedContainer.querySelectorAll(FORM_INPUT_SELECTOR);
    origInputs.forEach((input, i) => {
      if (cloneInputs[i]) {
        if (input.value) cloneInputs[i].setAttribute('value', input.value);
        const dataVal = input.getAttribute('data-value');
        if (dataVal != null) cloneInputs[i].setAttribute('data-value', dataVal);
      }
    });
    const origTextareas = formContainer.querySelectorAll('textarea');
    const cloneTextareas = clonedContainer.querySelectorAll('textarea');
    origTextareas.forEach((ta, i) => {
      if (cloneTextareas[i] && ta.value) {
        cloneTextareas[i].textContent = ta.value;
      }
    });
    const origSelects = formContainer.querySelectorAll('select');
    const cloneSelects = clonedContainer.querySelectorAll('select');
    origSelects.forEach((select, i) => {
      if (cloneSelects[i]) {
        const opt = select.options[select.selectedIndex];
        if (opt) {
          Array.from(cloneSelects[i].options).forEach(o => o.removeAttribute('selected'));
          const cloneOpt = cloneSelects[i].options[select.selectedIndex];
          if (cloneOpt) cloneOpt.setAttribute('selected', 'selected');
        }
      }
    });
    const origCheckboxes = formContainer.querySelectorAll('input[type="checkbox"], input[type="radio"]');
    const cloneCheckboxes = clonedContainer.querySelectorAll('input[type="checkbox"], input[type="radio"]');
    origCheckboxes.forEach((cb, i) => {
      if (cloneCheckboxes[i]) {
        if (cb.checked) cloneCheckboxes[i].setAttribute('checked', 'checked');
        else cloneCheckboxes[i].removeAttribute('checked');
      }
    });
    return clonedContainer.innerHTML;
  };

  const handleApprove = async () => {
    console.log('handleApprove 被調用');
    try {
      const formContainer = getFormContainer();
      if (formContainer) {
        const validation = validateEFormRequiredFields(formContainer);
        if (!validation.valid) {
          const keyMap = { fill: 'Fill', upload: 'Upload', check: 'Check', select: 'Select' };
          const msg = validation.fieldLabel
            ? t(`eformValidation.please${keyMap[validation.messageType] || 'Fill'}Field`, { field: validation.fieldLabel })
            : t('eformValidation.pleaseFillRequiredFields');
          message.error(msg);
          validation.firstInvalidEl?.focus?.();
          validation.firstInvalidEl?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
          return;
        }
      }
      setApproving(true);
      const token = localStorage.getItem('token');
      
      console.log('Token:', token ? '存在' : '不存在');
      
      if (!token) {
        message.error(t('eformInstance.loginRequired'));
        navigate('/');
        return;
      }
      
      const approvedBy = getCurrentUsername();
      const filledHtmlCode = collectFilledHtmlFromForm();
      console.log('發送批准請求到:', `/api/eforminstances/${id}/approve`);
      console.log('請求數據:', { approvedBy, note: approvalNote, hasFilledHtmlCode: !!filledHtmlCode });
      
      const response = await fetch(`/api/eforminstances/${id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          approvedBy,
          note: approvalNote,
          filledHtmlCode: filledHtmlCode || undefined
        })
      });

      console.log('響應狀態:', response.status);
      console.log('響應狀態文本:', response.statusText);

      if (response.status === 401) {
        message.error(t('eformInstance.loginExpired'));
        localStorage.removeItem('token');
        localStorage.removeItem('userInfo');
        navigate('/');
        return;
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API 錯誤響應:', errorText);
        throw new Error(`批准表單失敗: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('批准成功，響應:', result);
      message.success(t('eformInstance.formApproved'));
      
      // 關閉 Modal
      setApprovalModalVisible(false);
      setApprovalNote('');
      
      // 重新獲取實例以更新狀態
      await fetchInstance();
      
      console.log('批准操作完成，界面已更新');
    } catch (error) {
      console.error('批准表單錯誤:', error);
      message.error(t('eformInstance.approveFailed') + ': ' + error.message);
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    console.log('handleReject 被調用');
    try {
      if (!(rejectNote || '').trim()) {
        message.error(t('eformInstance.rejectionReasonRequired'));
        return;
      }
      setRejecting(true);
      const token = localStorage.getItem('token');
      
      console.log('Token:', token ? '存在' : '不存在');
      
      if (!token) {
        message.error(t('eformInstance.loginRequired'));
        navigate('/');
        return;
      }
      
      const approvedBy = getCurrentUsername();
      const filledHtmlCode = collectFilledHtmlFromForm();
      console.log('發送拒絕請求到:', `/api/eforminstances/${id}/reject`);
      console.log('請求數據:', { approvedBy, note: rejectNote, hasFilledHtmlCode: !!filledHtmlCode });
      
      const response = await fetch(`/api/eforminstances/${id}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          approvedBy,
          note: rejectNote,
          filledHtmlCode: filledHtmlCode || undefined
        })
      });

      console.log('響應狀態:', response.status);
      console.log('響應狀態文本:', response.statusText);

      if (response.status === 401) {
        message.error(t('eformInstance.loginExpired'));
        localStorage.removeItem('token');
        localStorage.removeItem('userInfo');
        navigate('/');
        return;
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API 錯誤響應:', errorText);
        throw new Error(`拒絕表單失敗: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('拒絕成功，響應:', result);
      message.success(t('eformInstance.formRejected'));
      
      // 關閉 Modal
      setRejectModalVisible(false);
      setRejectNote('');
      
      // 重新獲取實例以更新狀態
      await fetchInstance();
      
      console.log('拒絕操作完成，界面已更新');
    } catch (error) {
      console.error('拒絕表單錯誤:', error);
      message.error(t('eformInstance.rejectFailed') + ': ' + error.message);
    } finally {
      setRejecting(false);
    }
  };

  // 直接儲存表單（不變更狀態）
  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const filledHtmlCode = collectFilledHtmlFromForm();
      const res = await fetch(`/api/eforminstances/${id}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ filledHtmlCode }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.error || `HTTP ${res.status}`);
      }
      message.success(t('documentLibrary.saveSuccess') || '儲存成功');
    } catch (err) {
      message.error((t('documentLibrary.saveFailed') || '儲存失敗') + `: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // 匯出 PDF
  const handleExportPdf = async () => {
    if (exportingPdf) return;
    setExportingPdf(true);
    try {
      const token = localStorage.getItem('token');
      const today = TimezoneUtils.formatDateTime(new Date(), 'YYYY-MM-DD');
      const safeName = (instance.formName || `eform_${id}`).replace(/[^\w\-]+/g, '_').slice(0, 80);
      await exportSingleEformInstanceToPdf({
        instanceId: id,
        token,
        fileName: `${safeName}_${today}.pdf`,
      });
      message.success(t('reports.exportSuccess') || '匯出成功');
    } catch (error) {
      if (error.message === 'META_FLOWS_NOT_SUPPORTED') {
        message.warning(t('documentLibrary.metaFlowsPdfNotSupported'));
      } else {
        message.error((t('reports.exportFailed') || '匯出失敗') + (error.message ? `: ${error.message}` : ''));
      }
    } finally {
      setExportingPdf(false);
    }
  };

  // 提交表單（Manual Fill 模式）
  const handleSubmitForm = async () => {
    try {
      console.log('handleSubmitForm 被調用');
      
      if (!instance?.urlToken) {
        message.error('缺少訪問令牌');
        return;
      }

      // 從 DOM 中獲取填寫後的表單內容
      const formContainer = getFormContainer();
      if (!formContainer) {
        console.error('找不到表單容器 .form-content-inner');
        message.error('無法獲取表單內容');
        return;
      }

      // 驗證必填欄位
      const validation = validateEFormRequiredFields(formContainer);
      if (!validation.valid) {
        const keyMap = { fill: 'Fill', upload: 'Upload', check: 'Check', select: 'Select' };
        const msg = validation.fieldLabel
          ? t(`eformValidation.please${keyMap[validation.messageType] || 'Fill'}Field`, { field: validation.fieldLabel })
          : t('eformValidation.pleaseFillRequiredFields');
        message.error(msg);
        validation.firstInvalidEl?.focus?.();
        validation.firstInvalidEl?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        return;
      }

      // 克隆容器以避免修改原始 DOM
      const clonedContainer = formContainer.cloneNode(true);
      
      // 獲取所有表單元素並將用戶輸入的值寫入 value 屬性（含金額、日期類型）
      const inputs = clonedContainer.querySelectorAll(FORM_INPUT_SELECTOR);
      inputs.forEach(input => {
        if (input.value) {
          input.setAttribute('value', input.value);
          console.log(`設置 input ${input.name || input.id} value:`, input.value);
        }
      });
      
      // 處理 textarea
      const textareas = clonedContainer.querySelectorAll('textarea');
      textareas.forEach(textarea => {
        if (textarea.value) {
          textarea.textContent = textarea.value;
          console.log(`設置 textarea ${textarea.name || textarea.id} value:`, textarea.value);
        }
      });
      
      // 處理 select
      const selects = clonedContainer.querySelectorAll('select');
      selects.forEach(select => {
        const selectedOption = select.options[select.selectedIndex];
        if (selectedOption) {
          // 移除所有 selected 屬性
          Array.from(select.options).forEach(opt => opt.removeAttribute('selected'));
          // 設置當前選中的 option
          selectedOption.setAttribute('selected', 'selected');
          console.log(`設置 select ${select.name || select.id} selected:`, selectedOption.value);
        }
      });
      
      // 處理 checkbox 和 radio
      const checkboxes = clonedContainer.querySelectorAll('input[type="checkbox"], input[type="radio"]');
      checkboxes.forEach(checkbox => {
        if (checkbox.checked) {
          checkbox.setAttribute('checked', 'checked');
          console.log(`設置 ${checkbox.type} ${checkbox.name || checkbox.id} checked`);
        } else {
          checkbox.removeAttribute('checked');
        }
      });
      
      // 獲取處理後的 HTML
      const filledHtmlCode = clonedContainer.innerHTML;
      
      console.log('提交表單，FilledHtmlCode 長度:', filledHtmlCode.length);
      console.log('FilledHtmlCode 前 500 字符:', filledHtmlCode.substring(0, 500));
      
      const response = await fetch(`/api/eforminstances/${id}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: instance.urlToken,
          filledHtmlCode: filledHtmlCode
        })
      });

      console.log('提交響應狀態:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        console.error('提交失敗，錯誤:', errorData);
        throw new Error(errorData.error || `提交失敗: ${response.status}`);
      }

      const result = await response.json();
      console.log('提交成功，結果:', result);
      message.success('表單提交成功！');
      
      // 更新實例狀態
      setInstance(prev => ({
        ...prev,
        status: 'Submitted',
        filledHtmlCode: filledHtmlCode
      }));
      
    } catch (error) {
      console.error('提交表單錯誤:', error);
      message.error('提交失敗: ' + error.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'orange';
      case 'Approved': return 'green';
      case 'Rejected': return 'red';
      case 'Submitted': return 'blue';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'Pending': return t('eformInstance.pendingApproval');
      case 'Approved': return approvalStatusLabels.approve;
      case 'Rejected': return approvalStatusLabels.reject;
      case 'Submitted': return '已提交';
      default: return status;
    }
  };

  if (loading) {
    console.log('EFormInstancePage 正在載入...');
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        width: '100vw',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 9999,
        backgroundColor: '#f5f5f5'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!instance) {
    console.log('EFormInstancePage 沒有實例數據');
    return (
      <div style={{ 
        padding: '20px',
        width: '100vw',
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 9999,
        backgroundColor: '#f5f5f5'
      }}>
        <Alert
          message={t('eformInstance.error')}
          description={t('eformInstance.formInstanceNotFound')}
          type="error"
          showIcon
        />
      </div>
    );
  }

  console.log('EFormInstancePage 渲染，實例狀態:', instance.status);
  console.log('Modal 狀態 - approvalModalVisible:', approvalModalVisible);
  console.log('Modal 狀態 - rejectModalVisible:', rejectModalVisible);

  return (
    <>
      {/* 全屏容器 */}
      <div style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: '#f5f5f5',
        zIndex: 9999,
        overflow: 'auto'
      }}>
        {/* 頂部審批按鈕 - 固定在頂部 */}
        <div className={isMobile ? 'eform-header-mobile' : ''} style={{ 
          position: 'sticky', 
          top: 0, 
          zIndex: 1000, 
          background: '#fff', 
          padding: isMobile ? '16px 20px' : '24px 32px',
          borderBottom: '1px solid #e8e8e8',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          marginBottom: '0',
          width: '100%'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: isMobile ? 16 : 20,
            maxWidth: '100%'
          }}>
            <div style={{ flex: '1', minWidth: isMobile ? 0 : '300px' }}>
              <h2 style={{ 
                margin: 0, 
                fontSize: isMobile ? '22px' : '28px', 
                fontWeight: '600', 
                color: '#262626',
                lineHeight: '1.2'
              }}>
                {instance.formName}
              </h2>
              <Tag color={getStatusColor(instance.status)} style={{ 
                marginTop: isMobile ? 8 : 12,
                fontSize: isMobile ? 14 : 14,
                padding: isMobile ? '6px 14px' : '4px 12px'
              }}>
                {getStatusText(instance.status)}
              </Tag>
            </div>
            
            <Space
              className="approval-buttons"
              size={isMobile ? 8 : 12}
              style={{ flexShrink: 0, flexWrap: 'wrap', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}
            >
              {eformExportable && (
                <Button
                  icon={<SaveOutlined />}
                  loading={saving}
                  disabled={approving || rejecting || exportingPdf}
                  onClick={handleSave}
                  style={{
                    borderRadius: 20,
                    height: isMobile ? 40 : 44,
                    padding: isMobile ? '0 18px' : '0 24px',
                    fontSize: isMobile ? '15px' : '15px',
                    fontWeight: '500',
                    background: 'rgba(255,255,255,0.95)',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                  }}
                >
                  {t('common.save') || '儲存'}
                </Button>
              )}
              {eformExportable && (
                <Button
                  icon={<FilePdfOutlined />}
                  loading={exportingPdf}
                  disabled={saving || approving || rejecting}
                  onClick={handleExportPdf}
                  style={{
                    borderRadius: 20,
                    height: isMobile ? 40 : 44,
                    padding: isMobile ? '0 18px' : '0 24px',
                    fontSize: isMobile ? '15px' : '15px',
                    fontWeight: '500',
                    background: 'rgba(255,255,255,0.95)',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                  }}
                >
                  {t('common.exportPdf') || '匯出 PDF'}
                </Button>
              )}
              {((instance.status === 'Pending') || (instance.isManualFill && instance.status === 'Submitted')) && (
                instance.isManualFill ? (
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={handleSubmitForm}
                    style={{
                      backgroundColor: instance.status === 'Submitted' ? '#52c41a' : '#1890ff',
                      borderColor: instance.status === 'Submitted' ? '#52c41a' : '#1890ff',
                      borderRadius: 20,
                      height: isMobile ? 40 : 44,
                      padding: isMobile ? '0 20px' : '0 28px',
                      fontSize: isMobile ? '15px' : '15px',
                      fontWeight: '500',
                    }}
                  >
                    {instance.status === 'Submitted' ? '重新提交' : '提交表單'}
                  </Button>
                ) : (
                  <>
                    <Button
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      loading={approving}
                      disabled={saving || rejecting || exportingPdf}
                      onClick={() => setApprovalModalVisible(true)}
                      style={{
                        backgroundColor: '#52c41a',
                        borderColor: '#52c41a',
                        borderRadius: 20,
                        height: isMobile ? 40 : 44,
                        padding: isMobile ? '0 20px' : '0 28px',
                        fontSize: isMobile ? '15px' : '15px',
                        fontWeight: '500',
                      }}
                    >
                      {approvalActionLabels.approve}
                    </Button>
                    <Button
                      danger
                      icon={<CloseCircleOutlined />}
                      loading={rejecting}
                      disabled={saving || approving || exportingPdf}
                      onClick={() => setRejectModalVisible(true)}
                      style={{
                        borderRadius: 20,
                        height: isMobile ? 40 : 44,
                        padding: isMobile ? '0 20px' : '0 28px',
                        fontSize: isMobile ? '15px' : '15px',
                        fontWeight: '500',
                      }}
                    >
                      {approvalActionLabels.reject}
                    </Button>
                  </>
                )
              )}
            </Space>
          </div>
        </div>

        {/* 主要內容區域 */}
        <div className={`main-content eform-instance-page ${isMobile ? 'eform-mobile' : ''}`} style={{ 
          padding: isMobile ? '0 0 24px 0' : '32px',
          minHeight: 'calc(100vh - 120px)',
          width: '100%'
        }}>
          <EFormViewerBody
            instance={instance}
            relatedAttachments={relatedAttachments}
            loadingAttachments={loadingAttachments}
            userTimezoneOffset={userTimezoneOffset}
            isMobile={isMobile}
            lightboxZIndex={10002}
            contentMinHeight={isMobile ? 200 : 600}
            workflowInstanceId={instance.workflowExecutionId || null}
            workflowStepExecutionId={instance.workflowStepExecutionId || null}
            onAttachmentsUploaded={loadRelatedAttachments}
            formContentSlot={
              <EFormContentViewer
                fillType={instance.fillType}
                htmlCode={instance.htmlCode || ''}
                fieldDisplaySettings={instance.fieldDisplaySettings}
                metaFlowJson={instance.metaFlowJson}
                htmlStyle={{
                  padding: isMobile ? '16px' : '32px',
                  minHeight: isMobile ? '200px' : '500px',
                  fontSize: isMobile ? '16px' : '16px',
                  border: '1px solid #d9d9d9',
                  backgroundColor: '#fff',
                  overflowX: isMobile ? 'auto' : undefined,
                }}
                fallback={
                  formIframeSrc ? (
                    <iframe
                      ref={formIframeRef}
                      src={formIframeSrc}
                      title={t('eformInstance.formContent')}
                      className="form-content-inner"
                      scrolling="no"
                      onLoad={() => {
                        setFormIframeLoaded(n => n + 1);
                        const iframe = formIframeRef.current;
                        const doc = iframe?.contentDocument;
                        if (doc) {
                          injectFlatpickrIntoIframe(doc);
                          const resizeIframe = () => {
                            const body = doc.body;
                            const html = doc.documentElement;
                            // 讀取內容原始寬度（縮放前）
                            const naturalWidth = Math.max(
                              body.scrollWidth, body.offsetWidth,
                              html.scrollWidth, html.offsetWidth, 1
                            );
                            const naturalHeight = Math.max(
                              body.scrollHeight, body.offsetHeight,
                              html.scrollHeight, html.offsetHeight, 300
                            );
                            const iframeClientWidth = iframe.clientWidth || iframe.offsetWidth || window.innerWidth;
                            // 手機上對寬版（A4等）HTML 進行等比縮放
                            if (window.innerWidth <= 768 && naturalWidth > iframeClientWidth * 1.05) {
                              const scale = iframeClientWidth / naturalWidth;
                              const existing = doc.getElementById('__wtf_mobile_scale__');
                              if (existing) existing.remove();
                              const scaleStyle = doc.createElement('style');
                              scaleStyle.id = '__wtf_mobile_scale__';
                              scaleStyle.textContent = `
                                html { overflow: hidden !important; }
                                body {
                                  transform-origin: top left !important;
                                  transform: scale(${scale.toFixed(4)}) !important;
                                  width: ${naturalWidth}px !important;
                                  max-width: none !important;
                                }
                              `;
                              doc.head.appendChild(scaleStyle);
                              if (iframe) iframe.style.height = Math.ceil(naturalHeight * scale + 20) + 'px';
                            } else {
                              if (iframe) iframe.style.height = naturalHeight + 'px';
                            }
                          };
                          resizeIframe();
                          setTimeout(resizeIframe, 400);
                          setTimeout(resizeIframe, 1200);
                        }
                      }}
                      style={{
                        width: '100%',
                        height: '300px',
                        border: 'none',
                        backgroundColor: '#fff',
                        display: 'block',
                        overflow: 'hidden',
                      }}
                    />
                  ) : (
                    instance?.htmlCode && !(instance.fillType === 'MetaFlows' && (instance.htmlCode || '').trim().startsWith('{'))
                      ? (
                        <div style={{ textAlign: 'center', padding: 48 }}>
                          <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
                        </div>
                      )
                      : undefined
                  )
                }
              />
            }
          />
        </div>

        {/* 批准 Modal */}
        <Modal
          title={approvalActionLabels.approve}
          open={approvalModalVisible}
          onCancel={() => {
            console.log('批准 Modal 被取消');
            setApprovalModalVisible(false);
          }}
          footer={[
            <Button key="cancel" onClick={() => {
              console.log('批准 Modal 取消按鈕被點擊');
              setApprovalModalVisible(false);
            }}>
              {t('eformInstance.cancel')}
            </Button>,
            <Button
              key="approve"
              type="primary"
              loading={approving}
              onClick={() => {
                console.log('批准 Modal 確定按鈕被點擊');
                handleApprove();
              }}
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
            >
              {approvalActionLabels.approve}
            </Button>,
          ]}
          styles={{
            wrapper: { zIndex: 10000 },
            mask: { zIndex: 9999 }
          }}
        >
          <div>
            <p>{t('eformInstance.confirmApproveForm')}</p>
            <TextArea
              rows={4}
              placeholder={t('eformInstance.approvalNotePlaceholder')}
              value={approvalNote}
              onChange={(e) => setApprovalNote(e.target.value)}
            />
          </div>
        </Modal>

        {/* 拒絕 Modal */}
        <Modal
          title={approvalActionLabels.reject}
          open={rejectModalVisible}
          onCancel={() => {
            console.log('拒絕 Modal 被取消');
            setRejectModalVisible(false);
          }}
          footer={[
            <Button key="cancel" onClick={() => {
              console.log('拒絕 Modal 取消按鈕被點擊');
              setRejectModalVisible(false);
            }}>
              {t('eformInstance.cancel')}
            </Button>,
            <Button
              key="reject"
              danger
              loading={rejecting}
              onClick={() => {
                console.log('拒絕 Modal 確定按鈕被點擊');
                handleReject();
              }}
            >
              {approvalActionLabels.reject}
            </Button>,
          ]}
          styles={{
            wrapper: { zIndex: 10000 },
            mask: { zIndex: 9999 }
          }}
        >
          <div>
            <p>{t('eformInstance.confirmRejectForm')}</p>
            <TextArea
              rows={4}
              placeholder={t('eformInstance.rejectReasonPlaceholder')}
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
            />
          </div>
        </Modal>
      </div>

      {/* 隱藏側邊欄的 CSS */}
      <style>{`
        /* 隱藏側邊欄 */
        .ant-layout-sider {
          display: none !important;
        }
        
        /* 調整主佈局 */
        .ant-layout {
          margin-left: 0 !important;
        }
        
        /* 確保全屏顯示 */
        body {
          overflow: hidden;
        }
        
        /* 手機版：更大字體、更大觸控區域 */
        .eform-mobile .form-content-card .ant-card-head-title,
        .eform-mobile .form-info-media-card .ant-tabs-tab {
          font-size: 18px !important;
        }
        .eform-mobile .form-content-inner,
        .eform-mobile .form-content-inner input,
        .eform-mobile .form-content-inner select,
        .eform-mobile .form-content-inner textarea,
        .eform-mobile .form-content-inner label {
          font-size: 18px !important;
        }
        .eform-mobile .form-content-inner input,
        .eform-mobile .form-content-inner select,
        .eform-mobile .form-content-inner .ant-input,
        .eform-mobile .form-content-inner .ant-select-selector {
          min-height: 48px !important;
          padding: 12px 16px !important;
        }
        .eform-mobile .approval-buttons .ant-btn {
          min-height: 48px !important;
          font-size: 17px !important;
        }
        .eform-mobile .ant-tabs-tab {
          padding: 14px 16px !important;
          font-size: 17px !important;
        }
        
        /* 響應式設計 */
        @media (max-width: 768px) {
          .ant-layout-sider {
            display: none !important;
          }
          
          .ant-layout {
            margin-left: 0 !important;
          }
          
          /* 手機版佈局調整 */
          .ant-card {
            margin-bottom: 16px !important;
          }
          
          /* 手機版網格佈局 */
          .grid-container {
            display: flex !important;
            flex-direction: column !important;
            gap: 16px !important;
          }
          
          /* 手機版：表單內容置頂，資訊 Tab 置底 */
          .form-content-card {
            order: 1 !important;
          }
          
          .form-info-media-card {
            order: 2 !important;
          }
          
          /* 手機版間距調整 */
          .main-content {
            padding: 16px !important;
          }
          
          /* 手機版按鈕調整 - 更大觸控區域 */
          .approval-buttons {
            flex-direction: row !important;
            gap: 12px !important;
            width: 100% !important;
          }
          
          .approval-buttons .ant-btn {
            flex: 1 !important;
            min-height: 48px !important;
            height: 48px !important;
            min-width: 0 !important;
            font-size: 17px !important;
          }
          
          /* 手機版表單內容緊湊化 */
          .form-content-card .ant-card-body {
            padding: 8px !important;
          }
          
          /* 手機版表單內容內部調整 */
          .form-content-card .ant-card-body > div {
            padding: 8px !important;
            border: none !important;
            border-radius: 4px !important;
            min-height: auto !important;
          }
          
          /* 手機版表單內容中的卡片調整 */
          .form-content-card .ant-card-body .ant-card {
            margin-bottom: 8px !important;
            border-radius: 4px !important;
          }
          
          .form-content-card .ant-card-body .ant-card .ant-card-body {
            padding: 8px 12px !important;
          }
          
          /* 手機版表單內容中的輸入框調整 */
          .form-content-card .ant-card-body .ant-input,
          .form-content-card .ant-card-body .ant-select-selector {
            border-radius: 4px !important;
            font-size: 14px !important;
          }
          
          /* 手機版表單內容中的標籤調整 */
          .form-content-card .ant-card-body .ant-card-head {
            padding: 0 8px !important;
            min-height: 32px !important;
          }
          
          .form-content-card .ant-card-body .ant-card-head-title {
            font-size: 14px !important;
            padding: 8px 0 !important;
          }
          
          /* 手機版表單內容中的間距調整 */
          .form-content-card .ant-card-body .ant-form-item {
            margin-bottom: 8px !important;
          }
          
          .form-content-card .ant-card-body .ant-form-item-label {
            padding-bottom: 4px !important;
          }
          
          .form-content-card .ant-card-body .ant-form-item-label > label {
            font-size: 13px !important;
            height: auto !important;
            line-height: 1.2 !important;
          }
          
          /* 手機版表單內容中的文字調整 */
          .form-content-card .ant-card-body {
            font-size: 14px !important;
            line-height: 1.4 !important;
          }
          
          /* 手機版表單內容中的邊框調整 */
          .form-content-card .ant-card-body .ant-card {
            border: 1px solid #f0f0f0 !important;
            box-shadow: none !important;
          }
          
          /* 手機版表單內容中的背景調整 */
          .form-content-card .ant-card-body .ant-card-head {
            background-color: #fafafa !important;
            border-bottom: 1px solid #f0f0f0 !important;
          }
          
          /* 手機版表單內容內部容器調整 */
          .form-content-inner {
            padding: 8px !important;
            min-height: auto !important;
            border: none !important;
            border-radius: 4px !important;
          }
          
          /* 手機版表單內容中的所有卡片緊湊化 */
          .form-content-inner .ant-card {
            margin-bottom: 8px !important;
            border-radius: 4px !important;
            border: 1px solid #f0f0f0 !important;
            box-shadow: none !important;
          }
          
          .form-content-inner .ant-card-body {
            padding: 8px 12px !important;
          }
          
          .form-content-inner .ant-card-head {
            padding: 0 8px !important;
            min-height: 32px !important;
            background-color: #fafafa !important;
            border-bottom: 1px solid #f0f0f0 !important;
          }
          
          .form-content-inner .ant-card-head-title {
            font-size: 14px !important;
            padding: 8px 0 !important;
          }
          
          /* 手機版表單內容中的輸入框緊湊化 */
          .form-content-inner .ant-input,
          .form-content-inner .ant-select-selector {
            border-radius: 4px !important;
            font-size: 14px !important;
            height: 32px !important;
          }
          
          /* 手機版表單內容中的表單項目緊湊化 */
          .form-content-inner .ant-form-item {
            margin-bottom: 8px !important;
          }
          
          .form-content-inner .ant-form-item-label {
            padding-bottom: 4px !important;
          }
          
          .form-content-inner .ant-form-item-label > label {
            font-size: 13px !important;
            height: auto !important;
            line-height: 1.2 !important;
          }
          
          /* 手機版表單內容中的文字緊湊化 */
          .form-content-inner {
            font-size: 14px !important;
            line-height: 1.4 !important;
          }
          
          /* 手機版表單內容置中和全寬度 */
          .form-content-card {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          
          .form-content-card .ant-card-body {
            width: 100% !important;
            padding: 8px !important;
            margin: 0 !important;
          }
          
          .form-content-inner {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 auto !important;
            padding: 8px !important;
            box-sizing: border-box !important;
          }
          
          /* 手機版表單內容中的卡片全寬度 */
          .form-content-inner .ant-card {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 0 8px 0 !important;
            box-sizing: border-box !important;
          }
          
          .form-content-inner .ant-card-body {
            width: 100% !important;
            padding: 8px 12px !important;
            box-sizing: border-box !important;
          }
          
          /* 手機版表單內容中的輸入框全寬度 */
          .form-content-inner .ant-input,
          .form-content-inner .ant-select,
          .form-content-inner .ant-select-selector {
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          
          /* 手機版表單內容中的表單項目全寬度 */
          .form-content-inner .ant-form-item {
            width: 100% !important;
            max-width: 100% !important;
            margin-bottom: 8px !important;
          }
          
          .form-content-inner .ant-form-item-control {
            width: 100% !important;
            max-width: 100% !important;
          }
          
          /* 手機版表單內容中的標籤全寬度 */
          .form-content-inner .ant-form-item-label {
            width: 100% !important;
            max-width: 100% !important;
            text-align: left !important;
          }
          
          /* 手機版表單內容中的標籤文字 */
          .form-content-inner .ant-form-item-label > label {
            width: 100% !important;
            text-align: left !important;
            font-size: 13px !important;
            height: auto !important;
            line-height: 1.2 !important;
          }
          
          /* 手機版表單內容中的卡片標題置中 */
          .form-content-inner .ant-card-head {
            text-align: center !important;
            width: 100% !important;
          }
          
          .form-content-inner .ant-card-head-title {
            text-align: center !important;
            width: 100% !important;
            font-size: 14px !important;
            padding: 8px 0 !important;
          }
          
          /* 手機版表單內容中的容器置中 */
          .form-content-inner > * {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 auto !important;
            box-sizing: border-box !important;
          }
        }
        
        /* 平板版調整 */
        @media (max-width: 1024px) and (min-width: 769px) {
          .grid-container {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
        }
      `}</style>
    </>
  );
};

export default EFormInstancePage;